// Onboarding clarity (ANCHOR-01 / ANCHOR-05) — anchor-placement prompt banner.
//
// When the server force-gates the player into placing an Anchor
// (`playerView.requires_anchor_placement === true`, T2/T3 and T8/T9), the
// board lights up with Sun-Yellow pulsing borders (AnchorPlacementOverlay) and
// Submit/Skip disable — but until now NOTHING told the player WHAT to do or
// WHY. The canon copy (`match.anchor_placement.prompt`) shipped in en/sv but
// rendered nowhere. This banner surfaces it, plus a subtitle that explains the
// reward AND the Hollow trade-off (ANCHOR-05) up front instead of only after
// the first consumption.
//
// Self-explanatory WITHOUT the tutorial: this is a permanent in-match
// affordance, NOT a one-shot onboarding hint — every player who hits the gate
// sees it, tutorial-skippers included.
//
// Motion/a11y: the banner is intentionally STATIC (the eligible cells already
// carry the pulse cue via AnchorPlacementOverlay, which is reduced-motion
// gated). role="status" + aria-live="polite" announces the gate to screen
// readers when it activates. pointer-events:none so taps pass through to the
// Pixi eligible-cell overlay beneath. Tokens only — no hardcoded color.

import type { JSX } from 'react'
import { FormattedMessage } from 'react-intl'

export interface AnchorPlacementPromptProps {
  readonly requiresAnchorPlacement: boolean
  /**
   * ANCHOR-07 — number of cells eligible to hold the anchor. Surfaced as an
   * SR-only line so keyboard/screen-reader players learn how many targets exist
   * (the visible cue is the Pixi pulse, which they can't perceive).
   */
  readonly eligibleCount?: number
  /**
   * ANCHOR-08 (ARCA-143 / ARCA-120) — when true, the banner shows a transient
   * "Anchor placed!" success confirmation instead of the placement instruction.
   * MatchScreen latches this for ~800ms after a successful placement so the
   * confirmation is visible even when the next server snapshot clears the gate
   * (and unmounts the Pixi overlay) almost immediately. Takes precedence over
   * `requiresAnchorPlacement` — the player has already acted, so we acknowledge
   * it rather than re-instructing.
   */
  readonly justPlaced?: boolean
}

export function AnchorPlacementPrompt({
  requiresAnchorPlacement,
  eligibleCount,
  justPlaced = false,
}: AnchorPlacementPromptProps): JSX.Element | null {
  // Stay mounted during the post-placement confirmation beat even after the
  // server has cleared the gate (requiresAnchorPlacement → false).
  if (!requiresAnchorPlacement && !justPlaced) return null

  return (
    <div
      data-screen-region="anchor-placement-prompt"
      data-anchor-placed={justPlaced ? 'true' : 'false'}
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        top: 'var(--space-3)',
        left: 'var(--space-4)',
        right: 'var(--space-4)',
        // Gameplay-gating instruction (and its success confirmation) must always
        // be visible — sit above CoachTip + the cooldown toast (both zIndex 100).
        zIndex: 110,
        // The eligible-cell taps are handled by the Pixi overlay below; the
        // banner must never intercept them.
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-1)',
        alignItems: 'center',
        textAlign: 'center',
        padding: 'var(--space-2) var(--space-4)',
        background: 'var(--color-surface-deep)',
        // Flip the accent to the confirm (green) token on success so the
        // banner reads unmistakably as "done", not "still waiting".
        border: justPlaced
          ? '3px solid var(--color-cta-confirm)'
          : '3px solid var(--color-cta-primary)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {justPlaced ? (
        <span
          style={{
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-16)',
            lineHeight: 1.2,
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage id="match.anchor_placement.placed" />
        </span>
      ) : (
        <>
          <span
            style={{
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-16)',
              lineHeight: 1.2,
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage id="match.anchor_placement.prompt" />
          </span>
          <span
            style={{
              fontFamily: 'var(--font-family-body)',
              fontWeight: 600,
              fontSize: 'var(--font-size-12)',
              lineHeight: 1.4,
              color: 'var(--color-text-secondary)',
            }}
          >
            <FormattedMessage id="match.anchor_placement.subtitle" />
          </span>
          {/* ANCHOR-07 — SR-only actionable count (the visible pulse on eligible
              cells is the sighted cue; this narrates the same for SR/keyboard). */}
          {typeof eligibleCount === 'number' && eligibleCount > 0 ? (
            <span className="sr-only">
              <FormattedMessage
                id="match.anchor_placement.eligible_count"
                values={{ count: eligibleCount }}
              />
            </span>
          ) : null}
        </>
      )}
    </div>
  )
}
